import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { SlotCompactInfo } from '../models';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { CompartmentRuleItem } from '../../../administration';
import {
  AllocationViewRuleBriefInitialState,
  AllocationViewRuleBriefLoad,
  AllocationViewRuleBriefSetCompartmentName,
  AllocationViewRuleBriefSetSlotCompactInfo,
} from './allocation-view-rule-brief.actions';
import { AllocationViewRuleBriefService } from './allocation-view-rule-brief.service';

export interface AllocationViewRuleBriefStateModel {
  rule: CompartmentRuleItem;
  compartmentId: number;
  compartmentName: string;
  hubId: number;
  hubName: string;
  loaded: boolean;
  slotCompactInfo: SlotCompactInfo;
}

const initialState: AllocationViewRuleBriefStateModel = {
  rule: null,
  compartmentId: null,
  compartmentName: null,
  hubId: null,
  hubName: null,
  loaded: false,
  slotCompactInfo: null,
};

@State({
  name: 'allocationViewRuleBrief',
  defaults: initialState,
})
@Injectable()
export class AllocationViewRuleBriefState {
  constructor(
    private allocationViewRuleBriefService: AllocationViewRuleBriefService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getLoaded(state: AllocationViewRuleBriefStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getRule(state: AllocationViewRuleBriefStateModel): CompartmentRuleItem {
    return state.rule;
  }

  @Selector()
  static getHubId(state: AllocationViewRuleBriefStateModel): number {
    return state.hubId;
  }

  @Selector()
  static getHubName(state: AllocationViewRuleBriefStateModel): string {
    return state.hubName;
  }

  @Selector()
  static getCompartmentId(state: AllocationViewRuleBriefStateModel): number {
    return state.compartmentId;
  }

  @Selector()
  static getCompartmentName(state: AllocationViewRuleBriefStateModel): string {
    return state.compartmentName;
  }

  @Selector()
  static getSlotCompactInfo(state: AllocationViewRuleBriefStateModel): SlotCompactInfo {
    return state.slotCompactInfo;
  }

  @Action(AllocationViewRuleBriefLoad)
  getRule(
    ctx: StateContext<AllocationViewRuleBriefStateModel>,
    { payload: { compartmentId, ruleId, hub } }: AllocationViewRuleBriefLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const { id, name } = hub;
    return this.allocationViewRuleBriefService.getRule(ruleId, id, compartmentId).pipe(
      map((rule: CompartmentRuleItem) => {
        ctx.patchState({
          rule,
          hubId: id,
          hubName: name,
          compartmentId,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(AllocationViewRuleBriefSetCompartmentName)
  setCompartmentName(
    ctx: StateContext<AllocationViewRuleBriefStateModel>,
    { payload: { compartmentName } }: AllocationViewRuleBriefSetCompartmentName,
  ): void {
    ctx.patchState({ compartmentName });
  }

  @Action(AllocationViewRuleBriefSetSlotCompactInfo)
  setSlotCompactInfo(
    ctx: StateContext<AllocationViewRuleBriefStateModel>,
    { payload }: AllocationViewRuleBriefSetSlotCompactInfo,
  ): void {
    ctx.patchState({ slotCompactInfo: payload });
  }

  @Action(AllocationViewRuleBriefInitialState)
  setInitialState(ctx: StateContext<AllocationViewRuleBriefStateModel>): void {
    ctx.setState(initialState);
  }
}
